import { ApiError, request } from "../lib/api.js";

/** An export that was refused (e.g. pages without artwork); `missing` are 1-based page numbers. */
export class ExportBlockedError extends ApiError {
  readonly missing: number[];
  constructor(status: number, message: string, missing: number[]) {
    super(status, message);
    this.name = "ExportBlockedError";
    this.missing = missing;
  }
}
import type {
  BookPatch,
  CharacterData,
  ColoringBook,
  ColoringCharacter,
  ColoringJob,
  ColoringPage,
  ColoringState,
  ColoringVariant,
  CostEstimate,
  ExportKind,
  ExportSummary,
  FixResult,
  PageInput,
  PageOp,
  PreviewPlan,
  ProviderStatus,
  QualityReport
} from "./types.js";

export interface PagePromptInfo {
  auto: { prompt: string; negativePrompt: string };
  effective: { prompt: string; negativePrompt: string };
  custom: boolean;
}

export interface PlanResult {
  created: ColoringPage[];
  kept: number;
  removedStale: number;
  requested: number;
}

export const coloringApi = {
  // ---- AI text tasks ----
  developConceptDraft(body: {
    title?: string;
    idea: string;
    audience: string;
    difficulty: string;
    pageCount: number;
  }): Promise<{ brief: string }> {
    return request(`/coloring/develop-concept`, { method: "POST", body: JSON.stringify(body) });
  },
  developConcept(projectId: string): Promise<{ brief: string }> {
    return request(`/projects/${projectId}/coloring/develop-concept`, { method: "POST" });
  },
  generateStyleBible(projectId: string): Promise<{ book: ColoringBook }> {
    return request(`/projects/${projectId}/coloring/style-bible`, { method: "POST" });
  },
  suggestCharacters(projectId: string): Promise<{ created: ColoringCharacter[]; characters: ColoringCharacter[] }> {
    return request(`/projects/${projectId}/coloring/characters/suggest`, { method: "POST" });
  },
  planBook(projectId: string, body: { pageCount?: number; replaceUnstarted?: boolean } = {}): Promise<{ result: PlanResult; pages: ColoringPage[] }> {
    return request(`/projects/${projectId}/coloring/plan`, { method: "POST", body: JSON.stringify(body) });
  },
  pagePrompt(projectId: string, pageId: string): Promise<PagePromptInfo> {
    return request(`/projects/${projectId}/coloring/pages/${pageId}/prompt`);
  },
  imageUrl(projectId: string, variantId: string): string {
    return `/api/projects/${projectId}/coloring/images/${variantId}`;
  },

  // ---- Image generation + queue ----
  providers(projectId: string): Promise<ProviderStatus> {
    return request(`/projects/${projectId}/coloring/providers`);
  },
  estimate(projectId: string, count: number): Promise<CostEstimate> {
    return request(`/projects/${projectId}/coloring/estimate?count=${count}`);
  },
  generatePage(projectId: string, pageId: string, op: PageOp = "generate", instruction?: string): Promise<{ jobs: ColoringJob[] }> {
    return request(`/projects/${projectId}/coloring/pages/${pageId}/generate`, {
      method: "POST",
      body: JSON.stringify({ op, instruction: instruction ?? null })
    });
  },
  generateAll(projectId: string, body: { pageIds?: string[]; onlyMissing?: boolean } = {}): Promise<{ queued: number; skipped: number }> {
    return request(`/projects/${projectId}/coloring/generate`, { method: "POST", body: JSON.stringify(body) });
  },
  characterSheet(projectId: string, characterId: string): Promise<{ jobs: ColoringJob[] }> {
    return request(`/projects/${projectId}/coloring/characters/${characterId}/sheet`, { method: "POST" });
  },
  generateCover(projectId: string, side: "front" | "back"): Promise<{ jobs: ColoringJob[] }> {
    return request(`/projects/${projectId}/coloring/cover/generate`, { method: "POST", body: JSON.stringify({ side }) });
  },
  cancelQueue(projectId: string): Promise<{ canceled: number }> {
    return request(`/projects/${projectId}/coloring/queue/cancel`, { method: "POST" });
  },
  retryFailed(projectId: string): Promise<{ requeued: number }> {
    return request(`/projects/${projectId}/coloring/queue/retry-failed`, { method: "POST" });
  },
  clearQueue(projectId: string): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/coloring/queue/clear`, { method: "POST" });
  },
  pageVariants(projectId: string, pageId: string): Promise<{ variants: ColoringVariant[] }> {
    return request(`/projects/${projectId}/coloring/pages/${pageId}/variants`);
  },
  useVariant(projectId: string, pageId: string, variantId: string): Promise<{ page: ColoringPage }> {
    return request(`/projects/${projectId}/coloring/pages/${pageId}/use-variant`, { method: "POST", body: JSON.stringify({ variantId }) });
  },
  approvePage(projectId: string, pageId: string, approved: boolean): Promise<{ page: ColoringPage }> {
    return request(`/projects/${projectId}/coloring/pages/${pageId}/approve`, { method: "POST", body: JSON.stringify({ approved }) });
  },
  uploadPageImage(projectId: string, pageId: string, dataUrl: string): Promise<{ page: ColoringPage }> {
    return request(`/projects/${projectId}/coloring/pages/${pageId}/upload`, { method: "POST", body: JSON.stringify({ dataUrl }) });
  },
  // ---- Cover ----
  coverVariants(projectId: string): Promise<{ variants: ColoringVariant[] }> {
    return request(`/projects/${projectId}/coloring/cover/variants`);
  },
  useCoverVariant(projectId: string, variantId: string): Promise<{ book: ColoringBook }> {
    return request(`/projects/${projectId}/coloring/cover/use`, { method: "POST", body: JSON.stringify({ variantId }) });
  },
  uploadCover(projectId: string, side: "front" | "back", dataUrl: string): Promise<{ book: ColoringBook }> {
    return request(`/projects/${projectId}/coloring/cover/upload`, { method: "POST", body: JSON.stringify({ side, dataUrl }) });
  },
  clearCover(projectId: string, side: "front" | "back"): Promise<{ book: ColoringBook }> {
    return request(`/projects/${projectId}/coloring/cover/clear`, { method: "POST", body: JSON.stringify({ side }) });
  },

  // ---- Preview + export ----
  previewPlan(projectId: string): Promise<PreviewPlan> {
    return request(`/projects/${projectId}/coloring/preview`);
  },
  exportSummary(projectId: string): Promise<ExportSummary> {
    return request(`/projects/${projectId}/coloring/export/summary`);
  },
  pageDownloadUrl(projectId: string, pageId: string): string {
    return `/api/projects/${projectId}/coloring/export/page/${pageId}`;
  },
  /** Download an export. Throws ExportBlockedError (with the empty page numbers) when pages lack artwork. */
  async downloadExport(projectId: string, kind: ExportKind, skipMissing = false): Promise<{ notes: string[]; filename: string }> {
    const res = await fetch(`/api/projects/${projectId}/coloring/export?kind=${kind}${skipMissing ? "&skipMissing=1" : ""}`, { credentials: "same-origin" });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string; missing?: number[] };
      throw new ExportBlockedError(res.status, body.message ?? `Export failed (${res.status})`, body.missing ?? []);
    }
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `coloring-book-${kind}`;
    let notes: string[] = [];
    try {
      notes = JSON.parse(decodeURIComponent(res.headers.get("X-Export-Notes") ?? "[]")) as string[];
    } catch {
      notes = [];
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { notes, filename };
  },

  checkQuality(projectId: string, pageId: string): Promise<{ report: QualityReport; page: ColoringPage }> {
    return request(`/projects/${projectId}/coloring/pages/${pageId}/quality`, { method: "POST" });
  },
  fixQuality(projectId: string, pageId: string): Promise<FixResult> {
    return request(`/projects/${projectId}/coloring/pages/${pageId}/quality/fix`, { method: "POST" });
  },

  state(projectId: string): Promise<ColoringState> {
    return request(`/projects/${projectId}/coloring`);
  },
  updateBook(projectId: string, patch: BookPatch): Promise<{ book: ColoringBook }> {
    return request(`/projects/${projectId}/coloring`, { method: "PATCH", body: JSON.stringify(patch) });
  },

  createCharacter(projectId: string, input: { name?: string; data?: Partial<CharacterData> } = {}): Promise<{ character: ColoringCharacter }> {
    return request(`/projects/${projectId}/coloring/characters`, { method: "POST", body: JSON.stringify(input) });
  },
  updateCharacter(
    projectId: string,
    id: string,
    patch: { name?: string; data?: Partial<CharacterData> }
  ): Promise<{ character: ColoringCharacter }> {
    return request(`/projects/${projectId}/coloring/characters/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  deleteCharacter(projectId: string, id: string): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/coloring/characters/${id}`, { method: "DELETE" });
  },

  createPage(projectId: string, input: PageInput = {}): Promise<{ page: ColoringPage }> {
    return request(`/projects/${projectId}/coloring/pages`, { method: "POST", body: JSON.stringify(input) });
  },
  updatePage(projectId: string, id: string, patch: PageInput): Promise<{ page: ColoringPage }> {
    return request(`/projects/${projectId}/coloring/pages/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  deletePage(projectId: string, id: string): Promise<{ success: boolean }> {
    return request(`/projects/${projectId}/coloring/pages/${id}`, { method: "DELETE" });
  },
  reorderPages(projectId: string, ids: string[]): Promise<{ pages: ColoringPage[] }> {
    return request(`/projects/${projectId}/coloring/page-order`, { method: "PUT", body: JSON.stringify({ ids }) });
  }
};
